import React from 'react';
import { X, ShoppingBag, Tag, Palette, Star, Crown, Zap } from 'lucide-react';

const rarityIcons = {
  Common: null,
  Uncommon: <Zap className="w-4 h-4 text-green-400" />,
  Rare: <Star className="w-4 h-4 text-blue-400" />,
  Legendary: <Crown className="w-4 h-4 text-purple-400" />
};

const rarityColors = {
  Common: 'bg-gray-500/20 text-gray-300',
  Uncommon: 'bg-green-500/20 text-green-300',
  Rare: 'bg-blue-500/20 text-blue-300',
  Legendary: 'bg-purple-500/20 text-purple-300'
};

const colorDots = {
  black: 'bg-gray-900',
  white: 'bg-gray-100',
  gray: 'bg-gray-400',
  blue: 'bg-blue-500',
  green: 'bg-green-500',
  khaki: 'bg-yellow-600',
  olive: 'bg-green-700',
  navy: 'bg-blue-800',
  gold: 'bg-yellow-500',
  silver: 'bg-gray-300'
};

export default function ItemDrawer({ item, isOpen, onClose }) {
  if (!item) return null;

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40"
          onClick={onClose}
        />
      )}
      
      {/* Drawer */}
      <div className={`fixed right-0 top-0 h-full w-96 bg-[#111111] ring-1 ring-white/5 transform transition-transform duration-300 ease-in-out z-50 ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#1E2530]">
          <h2 className="text-xl font-semibold text-[#E7EDF5]">Item Details</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-[#0C0F14] border border-[#1E2530] text-[#A8B3C2] hover:bg-[#1E2530] hover:text-[#E7EDF5] transition-colors flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Preview Image */}
          <div className="relative aspect-square bg-gradient-to-br from-white/5 to-black rounded-xl overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center">
              <ShoppingBag className="w-16 h-16 text-white/40" />
            </div>
          </div>
          
          {/* Item Info */}
          <div className="space-y-4">
            <div>
              <h3 className="text-xl font-semibold text-[#E7EDF5] mb-1">{item.name}</h3>
              {item.brand && (
                <p className="text-[#A8B3C2] flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  {item.brand}
                </p>
              )}
            </div>
            
            {/* Meta Info */}
            <div className="grid grid-cols-2 gap-4">
              {item.color && (
                <div className="flex items-center gap-2">
                  <Palette className="w-4 h-4 text-white/70" />
                  <span className="text-white/70 text-sm">Color</span>
                  <span className="text-white capitalize">{item.color}</span>
                </div>
              )}
              
              {item.rarity && (
                <div className="flex items-center gap-2">
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${rarityColors[item.rarity]}`}>
                    <div className="flex items-center gap-1">
                      {rarityIcons[item.rarity]}
                      {item.rarity}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Price */}
            {item.price && (
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10">
                <span className="text-white/70">Price</span>
                <span className="text-2xl font-bold text-[#3CE17A]">${item.price}</span>
              </div>
            )}
            
            {/* Creator Royalty */}
            {item.royalty_bps !== undefined && item.royalty_bps !== null && (
              <div className="px-1">
                <span className="inline-block px-2 py-0.5 bg-green-500/10 border border-green-500/30 rounded-md text-green-400 text-xs">
                  {(item.royalty_bps / 100).toFixed(1)}% Creator Royalty
                </span>
              </div>
            )}
            
            {/* Social Links */}
            {(() => {
              // Get piece-specific social links (filled in during piece creation)
              const socialLinks = item.social_links || {};
              const hasSocialLinks = socialLinks.website || socialLinks.twitter || socialLinks.instagram || socialLinks.discord;
              
              if (!hasSocialLinks) return null;
              
              return (
                <div>
                  <h4 className="text-sm font-semibold text-white/70 mb-2">Social Links</h4>
                  <div className="flex items-center gap-2">
                    {socialLinks.website && (
                      <a
                        href={socialLinks.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors border border-white/10"
                        title="Website"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="2" y1="12" x2="22" y2="12"/>
                          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                        </svg>
                      </a>
                    )}
                    {socialLinks.twitter && (
                      <a
                        href={socialLinks.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors border border-white/10"
                        title="X (Twitter)"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                      </a>
                    )}
                    {socialLinks.instagram && (
                      <a
                        href={socialLinks.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors border border-white/10"
                        title="Instagram"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                          <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                        </svg>
                      </a>
                    )}
                    {socialLinks.discord && (
                      <a
                        href={socialLinks.discord}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors border border-white/10"
                        title="Discord"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                          <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                        </svg>
                      </a>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
          
          {/* Actions */}
          <div className="space-y-3 pt-4">
            <button 
              className="w-full py-3 bg-[#7C5CFC] text-white font-medium rounded-lg hover:bg-[#7C5CFC]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled
            >
              Add to Outfit
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
